import React from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./AlertDialog"

type ConfirmColor = "white" | "red"

interface ConfirmOptions {
  title: React.ReactNode
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  confirmColor?: ConfirmColor
  size?: "default" | "sm"
}

type Confirm = (options: ConfirmOptions) => Promise<boolean>

interface ActiveConfirm {
  options: ConfirmOptions
  resolve: (value: boolean) => void
}

const AlertDialogContext = React.createContext<Confirm | null>(null)

const AlertDialogProvider = ({ children }: { children: React.ReactNode }) => {
  const [active, setActive] = React.useState<ActiveConfirm | null>(null)
  const activeRef = React.useRef<ActiveConfirm | null>(null)
  const actionRef = React.useRef<HTMLButtonElement | null>(null)

  const resolveActive = React.useCallback((value: boolean) => {
    const current = activeRef.current
    if (!current) {
      return
    }
    activeRef.current = null
    setActive(null)
    current.resolve(value)
  }, [])

  const confirm = React.useCallback<Confirm>((options) => {
    if (activeRef.current) {
      return Promise.resolve(false)
    }
    return new Promise<boolean>((resolve) => {
      const entry = { options, resolve }
      activeRef.current = entry
      setActive(entry)
    })
  }, [])

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      resolveActive(false)
    }
  }

  return (
    <AlertDialogContext.Provider value={confirm}>
      {children}
      <AlertDialog open={active !== null} onOpenChange={handleOpenChange}>
        {active && (
          <AlertDialogContent size={active.options.size ?? "default"} initialFocus={actionRef}>
            <AlertDialogHeader>
              <AlertDialogTitle>{active.options.title}</AlertDialogTitle>
              {active.options.description && (
                <AlertDialogDescription render={<div />}>
                  {active.options.description}
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                color={active.options.confirmColor}
                onClick={() => resolveActive(true)}
                ref={actionRef}
              >
                {active.options.confirmLabel ?? "Confirm"}
              </AlertDialogAction>
              <AlertDialogCancel color="white">
                {active.options.cancelLabel ?? "Cancel"}
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </AlertDialogContext.Provider>
  )
}

export { AlertDialogContext, AlertDialogProvider }
export type { ConfirmOptions }
